// components/CleaningInfoModal.js
//
// Infovenster van een schoonmaakmoment voor de admin (datum, tijd, status,
// schoonmakers) met Verwijderen / Sluiten / Wijzigen. Gedeeld door de
// multikalender (PropertyTimeline) en het maandoverzicht in het beheerpaneel.
// De admin kan hier ook rechtstreeks schoonmakers toevoegen of verwijderen.
//
// info: { id, propertyName, start, end, status,
//         cleaners: [{ id, name }]   (in volgorde hoofd-, 2de, 3de schoonmaker),
//         expectedCount }
// onChanged(): geroepen na een gelukte wijziging, zodat de kalender zich ververst.
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import API_URL from '../config';
import Avatar from './Avatar';
import { getAuthToken } from '../utils/authSession';
import './CleaningInfoModal.css';

const timeOnly = (value, locale) => new Date(value).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
const MAX_CLEANERS = 3;
const LOCKED_STATUSES = ['COMPLETED', 'INVOICED', 'CANCELLED'];

function CleaningInfoModal({ info, locale, onClose, onDelete, onEdit, onChanged }) {
  const { t } = useTranslation();
  const [cleaners, setCleaners] = useState(() => (info?.cleaners || []).filter((c) => c && c.id));
  const [allCleaners, setAllCleaners] = useState([]);
  const [query, setQuery] = useState('');
  const [confirmRemoveId, setConfirmRemoveId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const locked = !info || LOCKED_STATUSES.includes(info.status);
  const infoId = info?.id;

  // Alle actieve schoonmakers ophalen (enkel de admin mag dit) voor het zoekveld.
  useEffect(() => {
    if (!infoId || locked) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/users`, { headers: { Authorization: `Bearer ${getAuthToken()}` } });
        if (!cancelled) setAllCleaners((res.data || []).filter((u) => u.role === 'SCHOONMAKER' && u.isActive));
      } catch (e) { /* stil: zonder lijst kan er enkel niet toegevoegd worden */ }
    })();
    return () => { cancelled = true; };
  }, [infoId, locked]);

  const takenIds = cleaners.map((c) => c.id);
  const photoOf = (c) => c.photo || allCleaners.find((u) => u.id === c.id)?.photoUrl || null;
  const matches = useMemo(() => {
    const term = query.trim().toLowerCase();
    return allCleaners
      .filter((u) => !takenIds.includes(u.id))
      .filter((u) => !term || `${u.name || ''} ${u.email || ''}`.toLowerCase().includes(term))
      .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'nl', { sensitivity: 'base' }))
      .slice(0, 6);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allCleaners, query, cleaners]);

  if (!info) return null;

  const headers = { headers: { Authorization: `Bearer ${getAuthToken()}` } };

  const addCleaner = async (user) => {
    if (busy || cleaners.length >= MAX_CLEANERS) return;
    setBusy(true);
    setError('');
    try {
      if (cleaners.length === 0) {
        await axios.post(`${API_URL}/api/cleaning-tasks/${info.id}/assign-cleaner`, { cleanerId: user.id }, headers);
      } else {
        const ids = [...cleaners.map((c) => c.id), user.id];
        await axios.put(`${API_URL}/api/cleaning-tasks/${info.id}/co-cleaners`, { cleanerId2: ids[1] || null, cleanerId3: ids[2] || null }, headers);
      }
      setCleaners((prev) => [...prev, { id: user.id, name: user.name || user.email, photo: user.photoUrl || null }]);
      setQuery('');
      if (onChanged) onChanged();
    } catch (e) {
      setError(e.response?.data?.error || t('calendar.infoCleanerError'));
    } finally {
      setBusy(false);
    }
  };

  const removeCleaner = async (cleanerId) => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      await axios.post(`${API_URL}/api/cleaning-tasks/${info.id}/remove-cleaner`, { cleanerId }, headers);
      setCleaners((prev) => prev.filter((c) => c.id !== cleanerId));
      setConfirmRemoveId(null);
      if (onChanged) onChanged();
    } catch (e) {
      setError(e.response?.data?.error || t('calendar.infoCleanerError'));
    } finally {
      setBusy(false);
    }
  };

  // Status volgt de bezetting: niemand = open, anders toegewezen (zoals de backend).
  const status = locked ? info.status : (cleaners.length === 0 ? 'OPEN' : 'ASSIGNED');
  const statusLabels = {
    OPEN: t('calendar.statusOpen'),
    PENDING_CONFIRMATION: t('calendar.statusPending'),
    WITHDRAWN: t('calendar.statusPending'),
    PENDING_APPROVAL: t('admin.pendingApprovalBadge'),
    ASSIGNED: t('calendar.statusAssigned'),
    COMPLETED: t('calendar.statusCompleted'),
    INVOICED: t('calendar.statusCompleted'),
    CANCELLED: t('calendar.statusCancelled'),
  };

  const day = new Date(info.start).toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' });
  // Zelfde kleuren als de balkjes in de agenda: rood = niemand toegewezen,
  // oranje = minimum niet bereikt, blauw = bezet, groen = voltooid.
  const done = ['COMPLETED', 'INVOICED'].includes(info.status) || new Date(info.end) < new Date();
  const hasCleaner = cleaners.length > 0;
  const partial = cleaners.length < Number(info.expectedCount);
  const tone = done ? 'done' : (!hasCleaner ? 'open' : (partial ? 'partial' : 'assigned'));

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal cleaning-info-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('calendar.cleaningInfoTitle')}</h2>
          <button type="button" className="modal-close" onClick={onClose}>✖</button>
        </div>
        <div className="cleaning-info-body">
          {info.propertyName && <div className="cleaning-info-property">{info.propertyName}</div>}
          <dl className="cleaning-info-list">
            <div className="cleaning-info-row">
              <dt>{t('calendar.infoDate')}</dt>
              <dd>{day}</dd>
            </div>
            <div className="cleaning-info-row">
              <dt>{t('calendar.infoTime')}</dt>
              <dd>{timeOnly(info.start, locale)} - {timeOnly(info.end, locale)}</dd>
            </div>
            <div className="cleaning-info-row">
              <dt>{t('calendar.infoStatus')}</dt>
              <dd><span className={`cleaning-info-status cleaning-info-status-${tone}`}>{statusLabels[status] || status}</span></dd>
            </div>
            <div className="cleaning-info-row">
              <dt>{t('calendar.infoCleaners')}</dt>
              <dd>
                {cleaners.length === 0 && <span className="cleaning-info-none">{t('calendar.infoNoCleaner')}</span>}
                {cleaners.length > 0 && (
                  <ul className="cleaning-info-cleaners">
                    {cleaners.map((c) => (
                      <li key={c.id} className="cleaning-info-cleaner">
                        <Avatar url={photoOf(c)} name={c.name} size={30} />
                        <span className="cleaning-info-cleaner-name">{c.name}</span>
                        {!locked && confirmRemoveId !== c.id && (
                          <button
                            type="button"
                            className="cleaning-info-cleaner-remove"
                            aria-label={t('calendar.infoRemoveCleaner')}
                            title={t('calendar.infoRemoveCleaner')}
                            disabled={busy}
                            onClick={() => setConfirmRemoveId(c.id)}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
                          </button>
                        )}
                        {!locked && confirmRemoveId === c.id && (
                          <span className="cleaning-info-cleaner-confirm">
                            <button type="button" className="cleaning-info-confirm-yes" disabled={busy} onClick={() => removeCleaner(c.id)}>{t('calendar.infoRemoveYes')}</button>
                            <button type="button" className="cleaning-info-confirm-no" disabled={busy} onClick={() => setConfirmRemoveId(null)}>{t('calendar.infoRemoveNo')}</button>
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}

                {!locked && cleaners.length < MAX_CLEANERS && (
                  <div className="cleaning-info-add">
                    <input
                      type="text"
                      className="cleaning-info-add-input"
                      placeholder={t('calendar.infoAddCleaner')}
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      disabled={busy}
                    />
                    {query.trim() !== '' && (
                      <ul className="cleaning-info-add-results">
                        {matches.length === 0 && <li className="cleaning-info-add-empty">{t('calendar.infoNoCleanersFound')}</li>}
                        {matches.map((u) => (
                          <li key={u.id}>
                            <button type="button" disabled={busy} onClick={() => addCleaner(u)}>
                              <Avatar url={u.photoUrl} name={u.name || u.email} size={30} />
                              <span className="cleaning-info-add-name">{u.name || u.email}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
                {error && <div className="cleaning-info-error">{error}</div>}
              </dd>
            </div>
          </dl>
        </div>
        <div className="cleaning-info-actions">
          <button type="button" className="cleaning-info-delete" onClick={() => onDelete(info.id)}>
            {t('common.delete')}
          </button>
          <button type="button" className="cancel-btn" onClick={onClose}>
            {t('common.close', 'Sluiten')}
          </button>
          <button type="button" className="submit-btn" onClick={() => onEdit(info.id)}>
            {t('common.edit')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CleaningInfoModal;
